import React from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Divider from '@amzn/meridian/divider';
import { getStatusAccentColor, getComparisonStatusFromVariance, formatWholeNumber } from '../utils';

interface MaestroCardProps {
    /** Uppercase card title, e.g. "VOLUME — DONE SO FAR". */
    heading: string;
    /** Big headline value (the "actual" so far). */
    actualValue: number | null;
    /** Plan/target value shown on the opaque "<planLabel>: X" line. */
    targetValue: number | null;
    /** Drives the top accent bar color. */
    comparisonStatus?: string;
    /** Suffix after the percentage, e.g. "to plan" / "to OP". */
    toLabel: string;
    /** Prefix for the plan line, e.g. "ALPS plan" / "OP plan" / "plan". */
    planLabel: string;
    /** Projected end-of-shift value; null renders an em dash. */
    projectedEos?: number | null;
    /** Projected range string, e.g. "88,000 – 99,000"; null renders an em dash. */
    projectedRange?: string | null;
    /**
     * Suffix after the projected-EOS percentage, e.g. "to ALPS". Defaults to
     * `toLabel` when omitted.
     */
    projectedToLabel?: string;
    /** "% to pace" (In-Shift live rail); rendered only when the server provides it. */
    pacePercentage?: number | null;
    /** Headcount cap ("cap at N"); HC card only, rendered when the server provides it. */
    capValue?: number | null;
    /**
     * Signed labor-hours to goal (HC card). Positive = surplus hours beyond what's
     * needed to hit target; negative = additional hours needed at current rate.
     * Rendered as the bottom section when `bottomVariant` is `'hoursDelta'`.
     */
    hoursDeltaToGoal?: number | null;
    /**
     * Which bottom section to render below the divider. `'projectedEos'` (default)
     * shows the PROJECTED EOS block; `'hoursDelta'` (HC card) shows HOURS DELTA TO GOAL;
     * `'none'` renders no bottom section (used where no projection is computed).
     */
    bottomVariant?: 'projectedEos' | 'hoursDelta' | 'none';
    /** Render actual / plan / projected values as whole numbers (volume cards). */
    wholeNumber?: boolean;
}

const numberFormatter = new Intl.NumberFormat('en-US');

const formatValue = (value: number | null | undefined, wholeNumber = false): string => {
    if (value == null) return '—';
    if (wholeNumber) return formatWholeNumber(value);
    return numberFormatter.format(Number.isInteger(value) ? value : Number(value.toFixed(2)));
};

/**
 * A single Maestro summary card (design refresh). Outline Box with a
 * status-colored top accent bar, a headline actual value with a "% to plan"
 * subtitle and an opaque plan line, then a divided "PROJECTED EOS" section.
 *
 * For now every card is fed the ALPS volume-target numbers (see MaestroCardsRow);
 * per-card metrics arrive when compareFlowMonitoringData is extended.
 */
const MaestroCard: React.FC<MaestroCardProps> = ({
    heading,
    actualValue,
    targetValue,
    comparisonStatus,
    toLabel,
    planLabel,
    projectedEos,
    projectedRange,
    projectedToLabel,
    pacePercentage,
    capValue,
    hoursDeltaToGoal,
    bottomVariant = 'projectedEos',
    wholeNumber = false,
}) => {
    const hasPlan = actualValue != null && targetValue != null && targetValue !== 0;
    const percentToPlan = hasPlan ? Math.round((actualValue! / targetValue!) * 100) : null;
    const accentColor = getStatusAccentColor(comparisonStatus);

    // Projected EOS vs. target: a percentage color-graded by how far the
    // projection lands from the target (green/amber/red), independent of the
    // server-computed actual-vs-plan status above.
    const hasProjectedPercent = projectedEos != null && targetValue != null && targetValue !== 0;
    const projectedPercent = hasProjectedPercent
        ? Math.round((projectedEos! / targetValue!) * 100)
        : null;
    const projectedColor = hasProjectedPercent
        ? getStatusAccentColor(getComparisonStatusFromVariance((projectedEos! / targetValue! - 1) * 100))
        : undefined;

    // HOURS DELTA TO GOAL (HC card): green when surplus (>= 0), red when hours are
    // still needed (< 0). A positive variance maps to the "ahead" status color.
    const hasHoursDelta = hoursDeltaToGoal != null;
    const hoursDeltaColor = hasHoursDelta
        ? getStatusAccentColor(getComparisonStatusFromVariance(hoursDeltaToGoal! >= 0 ? 100 : -100))
        : undefined;

    return (
        <Box type="outline">
            <Column spacing="none">
                <Box backgroundColor={accentColor} height={4} />
                <Box spacingInset="400">
                    <Column spacing="300">
                        <Column spacing="200">
                            <Text type="h100" color="secondary">{heading}</Text>
                            <Heading level={2} type="h500">{formatValue(actualValue, wholeNumber)}</Heading>
                            <Text type="b200" color="secondary">
                                {percentToPlan == null ? '—' : `${percentToPlan}% ${toLabel}`}
                            </Text>
                            <Text type="b200" color="secondary">
                                {planLabel}: {formatValue(targetValue, wholeNumber)}
                            </Text>
                            {pacePercentage != null && (
                                <Text type="b200" color="secondary">{pacePercentage}% to pace</Text>
                            )}
                            {capValue != null && (
                                <Text type="b200" color="secondary">cap at {formatValue(capValue)}</Text>
                            )}
                        </Column>
                        {bottomVariant !== 'none' && <Divider />}
                        {bottomVariant === 'none' ? null : bottomVariant === 'hoursDelta' ? (
                            <Column spacing="100">
                                <Text type="h100" color="secondary">HOURS DELTA TO GOAL</Text>
                                <Heading level={3} type="h400">
                                    <span style={{ color: hoursDeltaColor, fontWeight: 700 }}>
                                        {hasHoursDelta
                                            ? `${hoursDeltaToGoal! >= 0 ? '+' : ''}${formatValue(hoursDeltaToGoal)} hrs`
                                            : '—'}
                                    </span>
                                </Heading>
                                {hasHoursDelta && (
                                    <Text type="b200" color="secondary">
                                        {hoursDeltaToGoal! >= 0 ? 'surplus vs goal' : 'needed to hit goal'}
                                    </Text>
                                )}
                            </Column>
                        ) : (
                            <Column spacing="100">
                                <Text type="h100" color="secondary">PROJECTED EOS</Text>
                                <Row spacing="200" alignmentVertical="baseline">
                                    <Heading level={3} type="h400">{formatValue(projectedEos, wholeNumber)}</Heading>
                                    {projectedPercent != null && (
                                        <Text type="b200">
                                            <span style={{ color: projectedColor, fontWeight: 700 }}>
                                                {projectedPercent}% {projectedToLabel ?? toLabel}
                                            </span>
                                        </Text>
                                    )}
                                </Row>
                                <Text type="b200" color="secondary">
                                    range: {projectedRange ?? '—'}
                                </Text>
                            </Column>
                        )}
                    </Column>
                </Box>
            </Column>
        </Box>
    );
};

export default MaestroCard;
